/**
 * Lossless `Configuration` ↔ URL-hash codec.
 *
 * Hash shape: `#g=<id>&p=<pilot>&bw=<water>&b=<bugs>&mc=<mc>&w=<wind>&u=<m|i>`.
 * Decimals are pinned to each slider's step so every reachable position
 * round-trips exactly.
 * `decode` is tolerant: unknown/missing keys are skipped and out-of-range
 * numbers are clamped, so a hand-edited or partial hash never breaks the app.
 */
import { RANGES, type Configuration, type Units } from "./config";

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

export const encode = (c: Configuration): string => {
  const params = new URLSearchParams();
  params.set("g", c.gliderId);
  params.set("p", c.pilotWeight_kg.toFixed(0));
  params.set("bw", c.waterBallast_kg.toFixed(0));
  params.set("b", c.bugsPercent.toFixed(0));
  params.set("mc", c.macCready_ms.toFixed(1));
  params.set("w", c.windHeadwind_kmh.toFixed(0));
  params.set("u", c.units === "metric" ? "m" : "i");
  return `#${params.toString()}`;
};

const parseNumber = (raw: string | null): number | null => {
  if (raw === null) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

export const decode = (hash: string): Partial<Configuration> => {
  const params = new URLSearchParams(hash.replace(/^#/, ""));
  const out: {
    -readonly [K in keyof Configuration]?: Configuration[K];
  } = {};

  const g = params.get("g");
  if (g !== null && g !== "") out.gliderId = g;

  const p = parseNumber(params.get("p"));
  if (p !== null) {
    out.pilotWeight_kg = clamp(
      p,
      RANGES.pilotWeight_kg.min,
      RANGES.pilotWeight_kg.max,
    );
  }

  const bw = parseNumber(params.get("bw"));
  if (bw !== null) {
    out.waterBallast_kg = clamp(
      bw,
      RANGES.waterBallast_kg.min,
      RANGES.waterBallast_kg.max,
    );
  }

  const b = parseNumber(params.get("b"));
  if (b !== null) {
    out.bugsPercent = clamp(b, RANGES.bugsPercent.min, RANGES.bugsPercent.max);
  }

  const mc = parseNumber(params.get("mc"));
  if (mc !== null) {
    out.macCready_ms = clamp(mc, RANGES.macCready_ms.min, RANGES.macCready_ms.max);
  }

  const w = parseNumber(params.get("w"));
  if (w !== null) {
    out.windHeadwind_kmh = clamp(
      w,
      RANGES.windHeadwind_kmh.min,
      RANGES.windHeadwind_kmh.max,
    );
  }

  const u = params.get("u");
  if (u === "m" || u === "i") {
    out.units = (u === "m" ? "metric" : "imperial") satisfies Units;
  }

  return out;
};
